"use client";

import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Icon from "@/components/icons";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";

interface ResponsiveSliderProps {
  children: React.ReactNode[];
  className?: string;
  containerClassName?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  stacked?: boolean;
  defaultSlide?: number;
  desktopChildrenContainerClassName?: string;
  showInstructions?: boolean;
  navigationPosition?: "top" | "bottom";
}

export default function ResponsiveSlider({
  children,
  className = "",
  containerClassName = "",
  autoPlay = false,
  autoPlayInterval = 5000,
  stacked = false,
  defaultSlide = 0,
  desktopChildrenContainerClassName,
  showInstructions = true,
  navigationPosition = "top",
}: ResponsiveSliderProps) {
  const carouselMessages = useCarouselMessages();

  // Validate defaultSlide is within valid range
  const validDefaultSlide = Math.max(
    0,
    Math.min(defaultSlide, children.length - 1),
  );
  const [currentIndex, setCurrentIndex] = useState(validDefaultSlide);
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % children.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, children.length]);

  // Update transform for smooth sliding
  useEffect(() => {
    if (!slidesContainerRef.current) return;

    const translateX = -currentIndex * 100;

    if (!isInitialized) {
      // Set initial position without animation
      slidesContainerRef.current.style.transition = "none";
      slidesContainerRef.current.style.transform = `translateX(${translateX}%)`;
      // Force a reflow to ensure the transition is disabled
      slidesContainerRef.current.offsetHeight;
      // Re-enable transitions for future interactions
      slidesContainerRef.current.style.transition = "";
      setIsInitialized(true);
    } else {
      // Normal sliding with animation
      slidesContainerRef.current.style.transform = `translateX(${translateX}%)`;
    }
  }, [currentIndex, isInitialized]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % children.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + children.length) % children.length);
  };

  // Render navigation bar
  const renderNavigation = () => {
    if (children.length <= 1) return null;

    return (
      <>
        {showInstructions && (
          <div className="md:hidden pb-4 text-center text-base">
            Swipe left or right to review options.
          </div>
        )}
        <div className="flex justify-center mb-4 md:hidden">
          <div className="flex items-center bg-white rounded-xl border border-putty-400 px-2 py-2">
            {/* Left arrow */}
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className="mr-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
              disabled={currentIndex === 0}
            >
              <Icon
                type="chevron-left"
                svgProps={{
                  "aria-hidden": "true",
                  width: "24",
                  height: "24",
                }}
              />
              <span className="sr-only">{carouselMessages.previousSlide}</span>
            </Button>

            {/* Dots */}
            <div className="flex space-x-6">
              {children.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    "w-3.5 h-3.5 rounded-full transition-all duration-200",
                    index === currentIndex
                      ? "bg-brand-500"
                      : "bg-secondary-500 hover:bg-secondary-400",
                  )}
                  aria-label={carouselMessages.goToSlide(index + 1)}
                />
              ))}
            </div>

            {/* Right arrow */}
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="ml-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
              disabled={currentIndex === children.length - 1}
            >
              <Icon
                type="chevron-right"
                svgProps={{
                  "aria-hidden": "true",
                  width: "24",
                  height: "24",
                }}
              />
              <span className="sr-only">{carouselMessages.nextSlide}</span>
            </Button>
          </div>
        </div>
      </>
    );
  };

  // Handle touch/swipe events
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div className={cn("w-full", containerClassName)}>
      {/* Stacked layout for A/B testing */}
      {stacked ? (
        <>
          {/* Mobile/tablet stacked layout - hidden on desktop */}
          <div className={cn("md:hidden flex flex-col space-y-4", className)}>
            {children.map((child, index) => (
              <div key={index} className="w-full">
                {child}
              </div>
            ))}
          </div>

          {/* Desktop layout column grid - hidden on mobile/tablet */}
          <div
            className={cn(
              `hidden md:grid md:grid-cols-${children.length} gap-6`,
              className,
            )}
          >
            {children.map((child, index) => (
              <div key={index} className="w-full">
                {child}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Navigation bar - position controlled by navigationPosition prop */}
          {navigationPosition === "top" && renderNavigation()}

          {/* Desktop layout (dynamic grid) - hidden on mobile */}
          <div
            className={cn(
              "hidden md:grid gap-6 items-stretch",
              `md:grid-cols-${children.length}`,
              className,
            )}
          >
            {children.map((child, index) => (
              <div
                key={index}
                className={cn(
                  "w-full h-full",
                  desktopChildrenContainerClassName,
                )}
              >
                {child}
              </div>
            ))}
          </div>

          {/* Mobile/tablet layout (slider) - hidden on desktop */}
          <div className={cn("md:hidden w-full relative")}>
            {/* Slider container */}
            <div
              ref={containerRef}
              className={cn("relative overflow-hidden", className)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                ref={slidesContainerRef}
                className="flex transition-transform duration-300 ease-in-out"
              >
                {children.map((child, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0"
                    style={{ minWidth: "100%" }}
                  >
                    {child}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation bar - position controlled by navigationPosition prop */}
          {navigationPosition === "bottom" && renderNavigation()}
        </>
      )}
    </div>
  );
}
